import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { QuoteField } from '../../data/protocol/types';
import { useQuote } from '../../data/stores/quoteStore';
import { QUOTE_COLUMNS } from './quoteColumns';

export type SortableQuoteField =
  | 'volume'
  | 'change'
  | 'changePct';

export type QuoteSortState = {
  field: SortableQuoteField;
  direction: 'asc' | 'desc';
} | null;

const HEADER_HEIGHT = 30;

const SORTABLE_FIELDS = new Set<QuoteField>([
  'volume',
  'change',
  'changePct'
]);

interface QuoteTableProps {
  symbols: string[];
  fields: QuoteField[];
  rowHeight: number;
  sort: QuoteSortState;
  viewKey: string;
  emptyMessage?: string;
  onSort: (field: SortableQuoteField) => void;
  onSymbolDoubleClick: (symbol: string) => void;
}

export function QuoteTable({
  symbols,
  fields,
  rowHeight,
  sort,
  viewKey,
  emptyMessage = 'No symbols available.',
  onSort,
  onSymbolDoubleClick
}: QuoteTableProps) {
  const scrollRef =
    useRef<HTMLDivElement | null>(null);

  const columns = useMemo(() => {
    return fields.map(
      (field) => QUOTE_COLUMNS[field]
    );
  }, [fields]);

  const gridTemplateColumns = useMemo(() => {
    return columns
      .map((column) => `${column.widthPx}px`)
      .join(' ');
  }, [columns]);

  const tableWidth = useMemo(() => {
    return columns.reduce(
      (total, column) =>
        total + column.widthPx,
      0
    );
  }, [columns]);

  const rowVirtualizer = useVirtualizer({
    count: symbols.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    getItemKey: (index) =>
      symbols[index] ?? index,
    overscan: 18
  });

  const virtualizerRef = useRef(
    rowVirtualizer
  );

  virtualizerRef.current = rowVirtualizer;

  useLayoutEffect(() => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    scrollElement.scrollTop = 0;

    virtualizerRef.current.scrollToOffset(
      0,
      {
        align: 'start'
      }
    );

    virtualizerRef.current.measure();
  }, [viewKey]);

  const contentHeight =
    HEADER_HEIGHT +
    rowVirtualizer.getTotalSize();

  return (
    <div
      ref={scrollRef}
      className="quote-table no-window-drag"
    >
      <div
        className="quote-table__inner"
        style={{
          height: contentHeight,
          width: tableWidth
        }}
      >
        <div
          className="quote-table__header"
          style={{
            gridTemplateColumns,
            width: tableWidth
          }}
        >
          {columns.map((column) => {
            const sortable =
              SORTABLE_FIELDS.has(column.field);

            const active =
              sortable &&
              sort?.field === column.field;

            const direction = active
              ? sort?.direction
              : undefined;

            return (
              <div
                key={column.field}
                className={`quote-table__cell quote-table__cell--${column.align ?? 'left'}`}
              >
                {sortable ? (
                  <button
                    className={`quote-table__sort ${
                      active
                        ? 'quote-table__sort--active'
                        : ''
                    }`}
                    onClick={() =>
                      onSort(
                        column.field as SortableQuoteField
                      )
                    }
                    aria-sort={
                      active
                        ? direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <span>
                      {column.label}
                    </span>

                    <span className="quote-table__sort-glyph">
                      {active
                        ? direction === 'asc'
                          ? '▲'
                          : '▼'
                        : '↕'}
                    </span>
                  </button>
                ) : (
                  column.label
                )}
              </div>
            );
          })}
        </div>

        {rowVirtualizer
          .getVirtualItems()
          .map((virtualRow) => {
            const symbol =
              symbols[virtualRow.index];

            if (!symbol) {
              return null;
            }

            return (
              <QuoteRow
                key={symbol}
                symbol={symbol}
                fields={fields}
                gridTemplateColumns={
                  gridTemplateColumns
                }
                width={tableWidth}
                top={
                  HEADER_HEIGHT +
                  virtualRow.start
                }
                height={virtualRow.size}
                onDoubleClick={
                  onSymbolDoubleClick
                }
              />
            );
          })}
      </div>

      {symbols.length === 0 && (
        <div
          className="quote-table__empty"
          role="status"
          aria-live="polite"
        >
          <strong>No symbols</strong>
          <span>{emptyMessage}</span>
        </div>
      )}
    </div>
  );
}

interface QuoteRowProps {
  symbol: string;
  fields: QuoteField[];
  gridTemplateColumns: string;
  width: number;
  top: number;
  height: number;
  onDoubleClick: (symbol: string) => void;
}

const QuoteRow = memo(function QuoteRow({
  symbol,
  fields,
  gridTemplateColumns,
  width,
  top,
  height,
  onDoubleClick
}: QuoteRowProps) {
  const quote = useQuote(symbol);

  const previousTimeRef =
    useRef(quote.time);

  const timeoutRef =
    useRef<number | undefined>(undefined);

  const [flash, setFlash] =
    useState<'up' | 'down' | undefined>(
      undefined
    );

  useEffect(() => {
    if (
      quote.time === previousTimeRef.current
    ) {
      return;
    }

    previousTimeRef.current = quote.time;

    if (quote.direction === 0) {
      return;
    }

    setFlash(
      quote.direction > 0
        ? 'up'
        : 'down'
    );

    if (timeoutRef.current !== undefined) {
      window.clearTimeout(
        timeoutRef.current
      );
    }

    timeoutRef.current =
      window.setTimeout(() => {
        setFlash(undefined);
        timeoutRef.current = undefined;
      }, 520);
  }, [
    quote.time,
    quote.direction
  ]);

  useEffect(() => {
    return () => {
      if (
        timeoutRef.current !== undefined
      ) {
        window.clearTimeout(
          timeoutRef.current
        );
      }
    };
  }, []);

  const directionClass =
    quote.direction > 0
      ? 'quote-table__row--up'
      : quote.direction < 0
        ? 'quote-table__row--down'
        : '';

  const flashClass =
    flash === 'up'
      ? 'quote-table__row--flash-up'
      : flash === 'down'
        ? 'quote-table__row--flash-down'
        : '';

  return (
    <div
      className={`quote-table__row ${directionClass} ${flashClass}`}
      style={{
        transform: `translateY(${top}px)`,
        height,
        gridTemplateColumns,
        width
      }}
      onDoubleClick={() =>
        onDoubleClick(symbol)
      }
      role="button"
      tabIndex={0}
      title={`Double-click to open ${symbol} graph`}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onDoubleClick(symbol);
        }
      }}
    >
      {fields.map((field) => {
        const column =
          QUOTE_COLUMNS[field];

        const tone =
          column.tone?.(quote) ??
          'neutral';

        return (
          <div
            key={field}
            className={`quote-table__cell quote-table__cell--${column.align ?? 'left'} quote-table__cell--${tone}`}
          >
            {column.value(quote)}
          </div>
        );
      })}
    </div>
  );
});